import type React from 'react';
import { useTranslation } from 'react-i18next';

import BorderStyleDashed from '@/assets/icons/cg_border_style_dashed.svg?component';
import AlertLine from '@/assets/icons/ri_alert_line.svg?component';
import CheckboxCircleLine from '@/assets/icons/ri_checkbox_circle_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { alertIconColors } from '@/config/settings';
import type { CageHealthOverview, OverviewCage } from '@/domain/client';
import type { HealthParameterKey } from '@/domain/health';
import HealthLaksvelAlertsTooltipContent from '@/routes/Client/Cage/pages/Health/components/HealthLaksvelAlertsTooltipContent/HealthLaksvelAlertsTooltipContent';
import { getHealthMetricUiConfig } from '@/routes/Client/Cage/pages/Health/HealthDashboard/healthMetricConfig';
import { createCageUrl } from '@/utils/urlGenerator';

import { HealthOverviewCellLink } from './HealthOverviewCellLink';

import styles from './LocationOverviewLaksvelCell.module.scss';

interface LaksvelCellCenterProps {
    children: React.ReactNode;
}

function LaksvelCellCenter(props: LaksvelCellCenterProps) {
    return <div className={styles.laksvelCellCenter}>{props.children}</div>;
}

function resolveTopAlertParameterKey(health: CageHealthOverview): HealthParameterKey | undefined {
    if (health.topAlertParameterKey != null) {
        return health.topAlertParameterKey;
    }
    return health.alerts[0]?.parameterKey;
}

function getCellAlertLabel(
    health: CageHealthOverview,
    tGlobal: (key: string) => string,
    tHealth: (key: string, options?: { count: number }) => string
): string | null {
    const alertCount = health.alerts.length;
    if (alertCount === 0) {
        return null;
    }
    if (alertCount > 1) {
        return tHealth('locationOverview.alertCount', { count: alertCount });
    }
    const parameterKey = resolveTopAlertParameterKey(health);
    if (parameterKey == null) {
        return null;
    }
    return tGlobal(getHealthMetricUiConfig(parameterKey).labelKey);
}

export interface LocationOverviewLaksvelCellProps {
    cage: OverviewCage;
    clientId: number;
    locationId: number;
}

export default function LocationOverviewLaksvelCell(props: LocationOverviewLaksvelCellProps) {
    const { cage, clientId, locationId } = props;
    const { t: tGlobal } = useTranslation();
    const { t: tHealth } = useTranslation('healthModule');
    const health = cage.cageData.health;
    const healthDashboardLink = `${createCageUrl(clientId, locationId, cage.cageId)}/health`;

    if (health == null) {
        return (
            <LaksvelCellCenter>
                <Tooltip content={tGlobal('common.labels.noDataFound')} layout="fit">
                    <HealthOverviewCellLink
                        to={healthDashboardLink}
                        ariaLabel={tGlobal('common.labels.noDataFound')}
                        state="no-data">
                        <SvgIcon
                            icon={BorderStyleDashed}
                            size="custom"
                            customSize="1.7em"
                            color={alertIconColors.neutral}
                        />
                    </HealthOverviewCellLink>
                </Tooltip>
            </LaksvelCellCenter>
        );
    }

    const hasAlerts = health.alerts.length > 0;
    const cellLabel = hasAlerts ? getCellAlertLabel(health, tGlobal, tHealth) : null;
    const tooltipContent = <HealthLaksvelAlertsTooltipContent alerts={health.alerts} />;
    const ariaLabel =
        cellLabel != null
            ? `${tHealth('locationOverview.laksvelColumnHeader')}: ${cellLabel}`
            : tHealth('locationOverview.laksvelColumnHeader');

    const icon = hasAlerts ? AlertLine : CheckboxCircleLine;
    const iconColor = hasAlerts ? alertIconColors.warning : alertIconColors.success;

    return (
        <LaksvelCellCenter>
            <Tooltip content={tooltipContent} maxWidth={320} layout="fit">
                <HealthOverviewCellLink
                    to={healthDashboardLink}
                    ariaLabel={ariaLabel}
                    state={hasAlerts ? 'alert' : 'ok'}
                    className={styles.cellLink}>
                    <SvgIcon
                        icon={icon}
                        className={styles.cellIcon}
                        size="custom"
                        customSize={hasAlerts ? '1.4em' : '1.7em'}
                        color={iconColor}
                    />
                    {cellLabel != null ? (
                        <span className={styles.cellLabel}>{cellLabel}</span>
                    ) : null}
                </HealthOverviewCellLink>
            </Tooltip>
        </LaksvelCellCenter>
    );
}
